<script setup lang="ts">
import Modal from '@/components/common/layout/Modal.vue'
import { computed, inject, onMounted, ref, watch } from 'vue'
import type { CreateEventDto } from '@/api-swagger'
import { usePartnerStore } from '@/stores/usePartnerStore'
import { useEventStore } from '@/stores/useEventStore'
import { useContactStore } from '@/stores/useContactStore'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { removeTimeZone } from '@/utils/dateUtils'
import { useToast } from 'vue-toast-notification'
import { useUserStore } from '@/stores/useUserStore'
import '@vuepic/vue-datepicker/dist/main.css'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import TextInput from '@/components/common/inputs/TextInput.vue'
import SearchPartnerInput from '@/components/features/partners/SearchPartnerInput.vue'
import SelectWithAvatar from '@/components/common/selects/SelectWithAvatar.vue'
import SelectSimple from '@/components/common/selects/SelectSimple.vue'
import { PlusCircleIcon } from '@heroicons/vue/16/solid'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import SaveButton from '@/components/common/buttons/SaveButton.vue'
import { VQuillEditor } from '@morpheme/quill-editor'
import { useForm } from 'vee-validate'
import { object, string } from 'yup'
import DatetimePicker from '@/components/common/inputs/DatetimePicker.vue'
import type { Context } from '@/utils/types'
import ContactCreateModal from '@/components/features/contact/ContactCreateModal.vue'
import { differenceInHours } from 'date-fns'
import { useAttachmentStore } from '@/stores/useAttachmentStore'
import FileInput from '@/components/common/inputs/FileInput.vue'
import type { FileInputExpose } from '@/types/fileInput'

const attachmentStore = useAttachmentStore()
const attachmentRef = ref<FileInputExpose | null>(null)
const route = useRoute()
const partnerId: number | null = parseInt(route.params.partnerId as string) || null
const partnerStore = usePartnerStore()
const userStore = useUserStore()
const boostCoreStore = useBoostCorePermissionStore()
const eventStore = useEventStore()
const contactStore = useContactStore()
const { loading, categories } = storeToRefs(eventStore)
const { userConnected, users } = storeToRefs(userStore)
const { contacts } = storeToRefs(contactStore)
const { partner, partnerAddressForEventSelected } = storeToRefs(partnerStore)
const $toast = useToast()
const showModal = ref<boolean>(false)
const startDate = ref()
const endDate = ref()
const contactPartner = ref<number[]>([])
const contactUser = ref<number[]>([])
const event = ref<CreateEventDto>()
const choosenPartner = ref<number | undefined>(undefined)
const emit = defineEmits(['close'])
const context = inject<Context>('context', '')

onMounted(() => {
  if (userConnected.value === null) {
    userStore.getUserByEmail(boostCoreStore.user?.email)
  }
  eventStore.getAllCategoryEvent()

  if (partner.value) {
    fetchSuiteContacts(partner.value.id)
  }
  window.addEventListener('open-event-create-modal', openModal)
})

const schema = object({
  content: string().required().label('Content'),
})

const { values } = useForm({
  validationSchema: schema,
})

const errors = ref<Record<string, string>>({})

const requiredFields = [
  { field: 'name', label: 'sujet' },
  { field: 'categIds', label: 'étiquette' },
]

const onSubmit = async () => {
  errors.value = {}

  if (startDate.value && endDate.value && endDate.value <= startDate.value) {
    errors.value['endDate'] = 'La date de fin ne peut pas être avant la date de début.'
    return
  }

  requiredFields.forEach(({ field, label }) => {
    if (!event.value?.[field as keyof CreateEventDto]) {
      errors.value[field] = `Le champ ${label} est requis.`
    }
  })

  if (Object.keys(errors.value).length > 0) {
    return
  }

  if (event.value) {
    event.value.start = removeTimeZone(startDate.value)
    event.value.stop = removeTimeZone(endDate.value)

    event.value.description = values.description

    // Combine selected partners and users
    const selectedPartnerIds = [...contactPartner.value, ...contactUser.value]

    // Add the event creator (current user) to partnerIds if they have a partnerId
    if (
      userConnected.value?.partnerId &&
      !selectedPartnerIds.includes(userConnected.value.partnerId)
    ) {
      selectedPartnerIds.push(userConnected.value.partnerId)
    }

    // Add all selected partners to the event
    if (event.value.partnerSelected != null) {
      selectedPartnerIds.push(event.value.partnerSelected)
    }
    event.value!.partnerIds = selectedPartnerIds

    await eventStore
      .createEvent(event.value, context)
      .then((res) => {
        $toast.success('Rendez-vous créé avec succès.')
        if (attachmentRef.value) {
          const files = attachmentRef.value?.fileInputRef?.files
          if (files && files.length > 0) {
            const fileArray: File[] = Array.from(files)

            attachmentStore
              .uploadAttachments(fileArray, 'calendar.event', res?.id || 0)
              .then(() => {
                $toast.success('Pièces jointes enregistrées avec succès.')
              })
              .catch((error: { message: string }) => {
                $toast.error(
                  `Erreur lors de l'enregistrement des pièces jointes : ${error.message}`,
                )
              })
          }
        }
      })
      .catch((error) => {
        $toast.error(error.message)
      })
      .finally(() => {
        emit('close')
        closeModal()
      })
  }
}

const fetchSuiteContacts = async (odooPartnerId: number) => {
  const company = await partnerStore.fetchCompanyByOdooRef(odooPartnerId)
  if (company?.uuid) {
    await contactStore.fetchContacts({ companyUuid: [company.uuid] }, 1, 100)
  }
}

const openModal = async (e: any) => {
  let location = undefined
  if (!partnerId) {
    partner.value = null
  } else {
    await partnerStore.getPartnerAddressById(partnerId)
    await fetchSuiteContacts(partnerId)
    location =
      partnerAddressForEventSelected.value!.street +
      ' ' +
      partnerAddressForEventSelected.value!.zip +
      ' ' +
      partnerAddressForEventSelected.value!.city
  }

  event.value = {
    name: '',
    start: '',
    stop: '',
    partnerIds: [],
    categIds: 0,
    description: '',
    userId: userConnected.value?.id || 0,
    partnerSelected: partnerId,
    location: location,
    contextTracking: false,
  }
  contactUser.value = userConnected.value?.partnerId ? [userConnected.value.partnerId] : []

  if (partnerId) {
    const company = await partnerStore.fetchCompanyByOdooRef(partnerId)
    if (company) {
      preselectedCompany.value = { uuid: company.uuid, name: partner.value?.name || company.name }
    }
  }

  const now = e ? new Date(e.detail.date) : new Date()
  if (now.getHours() === 0) {
    now.setHours(new Date().getHours() + 1)
  }
  const nextFullHour = e ? new Date(now) : new Date(now.setMinutes(0, 0, 0))
  nextFullHour.setHours(nextFullHour.getHours() + 1)

  startDate.value = e ? new Date(now) : new Date(nextFullHour)
  endDate.value = e ? new Date(now) : new Date(nextFullHour)
  endDate.value.setHours(endDate.value.getHours() + 1)

  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  event.value = undefined
  contactPartner.value = []
  contactUser.value = []
  choosenPartner.value = undefined
  startDate.value = undefined
  endDate.value = undefined
}

watch(choosenPartner, async (newValue) => {
  if (newValue) {
    event.value!.partnerSelected = newValue
    await fetchSuiteContacts(newValue)
    await partnerStore.getPartnerAddressById(newValue)

    const company = await partnerStore.fetchCompanyByOdooRef(newValue)
    if (company) {
      preselectedCompany.value = { uuid: company.uuid, name: company.name }
    }

    event.value!.location =
      partnerAddressForEventSelected.value!.street +
      ' ' +
      partnerAddressForEventSelected.value!.zip +
      ' ' +
      partnerAddressForEventSelected.value!.city
  } else {
    contactPartner.value = []
    event.value!.location = ''
    preselectedCompany.value = undefined
  }
})

watch(startDate, (newValue, oldValue) => {
  // Quand la date de début change (sans l'heure), on modifie la date de fin
  if (newValue && oldValue) {
    if (
      newValue.getFullYear() !== oldValue.getFullYear() ||
      newValue.getMonth() !== oldValue.getMonth() ||
      newValue.getDate() !== oldValue.getDate()
    ) {
      const newDate = new Date(endDate.value)
      newDate.setFullYear(newValue.getFullYear())
      newDate.setMonth(newValue.getMonth())
      newDate.setDate(newValue.getDate())

      endDate.value = newDate
    }

    if (
      newValue.getHours() !== oldValue.getHours() ||
      newValue.getMinutes() !== oldValue.getMinutes()
    ) {
      const newDate = new Date(endDate.value)
      newDate.setHours(
        newValue.getHours() + differenceInHours(endDate.value, oldValue),
        newValue.getMinutes(),
      )
      endDate.value = newDate
    }
  }
})

const optionsChilds = computed(() => {
  return (
    contacts.value?.data?.filter((c) => !!c.referenceExterne).map((contact) => ({
      id: Number(contact.referenceExterne) || 0,
      text: [contact.firstname, contact.lastname].filter(Boolean).join(' ') || '',
      subText: contact.email || '',
    })) || []
  )
})

const optionsUsers = computed(() => {
  return (
    users?.value?.map((u) => {
      return {
        id: u.partnerId?.id || 0,
        text: u.name,
        subText: u.email || '',
      }
    }) || []
  )
})
const preselectedCompany = ref<{ uuid: string; name: string } | undefined>(undefined)

const handleCreateContact = async () => {
  if (choosenPartner.value || partnerId) {
    await fetchSuiteContacts(choosenPartner.value || partnerId || 0)
  }
}
</script>

<template>
  <slot name="trigger" :openModal="openModal">
    <ButtonDaisy text="Nouveau" :icon="PlusCircleIcon" variant="secondary" @click="openModal" />
  </slot>
  <Modal
    :showModal="showModal"
    @close="closeModal"
    title="Création d'un Rendez-vous"
    :loading="loading"
  >
    <template #content>
      <form @submit.prevent="" v-if="event" class="space-y-4">
        <div class="grid lg:grid-cols-3 gap-4">
          <div class="col-span-2">
            <TextInput
              v-model="event!.name"
              label="Sujet"
              placeholder="Sujet de rendez-vous"
              required
              :errorMessage="errors.name"
            />
          </div>
          <SelectSimple
            label="Étiquette"
            :options="
              categories?.map((category) => {
                return {
                  id: category.id.toString(),
                  value: category.id.toString(),
                  label: category.name,
                }
              })
            "
            v-model="event.categIds"
            required
            :errorMessage="errors.categIds"
          />
        </div>
        <div class="grid xl:grid-cols-2 gap-4">
          <div>
            <label class="label" for="description">
              <span class="label-text font-semibold"> Dates </span>
            </label>
            <DatetimePicker label="Début" v-model="startDate" :errorMessage="errors.startDate" />
            <DatetimePicker label="Fin" v-model="endDate" :errorMessage="errors.endDate" />
          </div>
        </div>
        <div class="grid grid-cols-12 gap-4">
          <TextInput
            class="col-span-6"
            v-if="partner"
            :model-value="partner?.name"
            label="Raison sociale"
            :disabled="true"
            required
          />
          <SearchPartnerInput class="col-span-6" v-model="choosenPartner" v-else />
          <TextInput class="col-span-6" v-model="event!.location" label="Lieu" placeholder="Lieu" />
          <SelectWithAvatar
            :class="{
              'col-span-5': choosenPartner || event.partnerSelected,
              'col-span-6': !choosenPartner && !event.partnerSelected,
            }"
            v-model="contactPartner"
            :options="optionsChilds"
            label="Participants Client"
            multiple
          />
          <div v-if="choosenPartner || partnerId" class="col-auto flex flex-col justify-end">
            <div class="flex item-center">
              <ContactCreateModal
                :preselectedCompany="preselectedCompany"
                lightweight
                @created="handleCreateContact"
              />
            </div>
          </div>
          <SelectWithAvatar
            class="col-span-6"
            v-model="contactUser"
            :options="optionsUsers"
            label="Collaborateurs"
            multiple
            required
          />

          <div class="flex items-center mt-2 gap-4">
            <div class="flex items-center">
              <input type="checkbox" v-model="event!.contextTracking" class="toggle toggle-xs" />
            </div>
            <div class="label">
              <span class="label-text font-semibold"> Envoyer invitation au participants </span>
            </div>
          </div>
        </div>
        <div>
          <label class="label" for="description">
            <span class="label-text font-semibold"> Description </span>
          </label>
          <VQuillEditor name="description" v-model="event!.description" placeholder="Description" />
        </div>

        <div class="flex justify-between gap-4">
          <FileInput ref="attachmentRef" label="Pièces jointes" :multiple="true" />

          <SaveButton :loading="loading" @save="onSubmit" />
        </div>
      </form>
    </template>
  </Modal>
</template>

<style>
.ql-editor {
  height: 125px !important;
}
</style>
